import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, KeyRound, Receipt, Unplug } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  useToast,
} from '@/components/ui';
import { ApiError, request } from '@/lib/api';

/* ---------------------------------------------------------------------------
   חשבונית ירוקה (ADR 0004): מפתח API ולא OAuth. הבעלים יוצר מפתח בחשבונית
   ירוקה (הגדרות ← כלי מפתחים ← מפתחות API) ומדביק כאן ID + Secret. השרת
   מאמת מול חשבונית ירוקה ורק אז שומר, מוצפן.
   --------------------------------------------------------------------------- */

const statusSchema = z.array(
  z.object({
    provider: z.string(),
    status: z.enum(['CONNECTED', 'EXPIRED', 'ERROR', 'DISCONNECTED']),
    lastError: z.string().nullish(),
  }),
);

const HELP_URL = 'https://www.greeninvoice.co.il/api-docs/';

export function GreenInvoiceCard({
  onConnected,
  description,
}: {
  onConnected?: () => void;
  description?: string;
}) {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const qc = useQueryClient();
  const [apiKeyId, setId] = useState('');
  const [apiKeySecret, setSecret] = useState('');

  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: ({ signal }) => request('/integrations', { schema: statusSchema, signal }),
  });
  const row = integrations.data?.find((i) => i.provider === 'GREEN_INVOICE');
  const status = row?.status ?? 'DISCONNECTED';

  const connect = useMutation({
    mutationFn: () =>
      request('/integrations/green-invoice', {
        method: 'POST',
        body: { apiKeyId: apiKeyId.trim(), apiKeySecret: apiKeySecret.trim() },
        schema: z.object({ connected: z.boolean(), environment: z.string(), demo: z.boolean().optional() }),
      }),
    onSuccess: (res) => {
      setId('');
      setSecret('');
      toast.success(
        t('greenInvoice.connected'),
        res.demo ? t('greenInvoice.demo') : res.environment === 'sandbox' ? t('greenInvoice.sandbox') : undefined,
      );
      void qc.invalidateQueries({ queryKey: ['integrations'] });
      void qc.invalidateQueries({ queryKey: ['onboarding-import'] });
      onConnected?.();
    },
    onError: (err: unknown) =>
      toast.error(t('greenInvoice.connectFailed'), err instanceof ApiError ? err.message : undefined),
  });

  const disconnect = useMutation({
    mutationFn: () => request('/integrations/green-invoice', { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('greenInvoice.disconnected'));
      void qc.invalidateQueries({ queryKey: ['integrations'] });
      void qc.invalidateQueries({ queryKey: ['onboarding-import'] });
    },
    onError: (err: unknown) =>
      toast.error(t('greenInvoice.disconnectFailed'), err instanceof ApiError ? err.message : undefined),
  });

  const connected = status === 'CONNECTED';
  const canSubmit = apiKeyId.trim().length >= 4 && apiKeySecret.trim().length >= 8;

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-success-subtle text-success">
            <Receipt className="size-4" aria-hidden />
          </span>
          <div>
            <CardTitle className="text-base">{t('greenInvoice.title')}</CardTitle>
            <CardDescription>{description ?? t('greenInvoice.description')}</CardDescription>
          </div>
        </div>
        {connected ? (
          <Badge tone="success" className="shrink-0 whitespace-nowrap">
            {t('greenInvoice.badgeConnected')}
          </Badge>
        ) : status === 'EXPIRED' || status === 'ERROR' ? (
          <Badge tone="warning" className="shrink-0 whitespace-nowrap">
            {t('greenInvoice.badgeReconnect')}
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {row?.lastError && !connected ? <p className="text-xs text-danger">{row.lastError}</p> : null}
        {connected ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-fg-muted">{t('greenInvoice.keyStored')}</p>
            <Button
              variant="secondary"
              size="sm"
              loading={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              <Unplug aria-hidden />
              {t('greenInvoice.disconnect')}
            </Button>
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) connect.mutate();
            }}
          >
            <ol className="list-decimal space-y-1 ps-5 text-xs text-fg-muted">
              <li>{t('greenInvoice.step1')}</li>
              <li>{t('greenInvoice.step2')}</li>
              <li>{t('greenInvoice.step3')}</li>
            </ol>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="API Key ID" htmlFor="gi-key-id">
                <Input
                  id="gi-key-id"
                  dir="ltr"
                  autoComplete="off"
                  spellCheck={false}
                  value={apiKeyId}
                  onChange={(e) => setId(e.target.value)}
                />
              </Field>
              <Field label="API Key Secret" htmlFor="gi-key-secret">
                <Input
                  id="gi-key-secret"
                  dir="ltr"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  value={apiKeySecret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </Field>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <a
                href={HELP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
              >
                <ExternalLink className="size-3.5" aria-hidden />
                {t('greenInvoice.guide')}
              </a>
              <Button type="submit" size="sm" loading={connect.isPending} disabled={!canSubmit} data-track="integration.green-invoice.connect">
                <KeyRound aria-hidden />
                {t('greenInvoice.connect')}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
